import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Briefcase, CheckCircle, Plus, X, Info, Sparkles, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { handleSaveError, saveDraft, clearDraft, draftScope } from '../../utils/saveErrorHandler';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const GigRolesEditor = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [primaryTitle, setPrimaryTitle] = useState('');
  const [newCustomRole, setNewCustomRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch categories and current selections
  const fetchData = useCallback(async () => {
    try {
      const [categoriesRes, profileRolesRes] = await Promise.all([
        api.get('/talent/gig-roles/categories'),
        api.get('/talent/profile/gig-roles')
      ]);
      
      setCategories(categoriesRes.data.categories || {});
      setSelectedRoles(profileRolesRes.data.gig_roles || []);
      setCustomRoles(profileRolesRes.data.custom_roles || []);
      setPrimaryTitle(profileRolesRes.data.primary_title || '');
    } catch (error) {
      toast.error('Failed to load gig roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if a role is selected
  const isRoleSelected = (category, role) => {
    return selectedRoles.some(r => r.category === category && r.role === role);
  };

  // Toggle a role selection
  const toggleRole = (category, role) => {
    setHasChanges(true);
    if (isRoleSelected(category, role)) {
      setSelectedRoles(prev => prev.filter(r => !(r.category === category && r.role === role)));
    } else {
      setSelectedRoles(prev => [...prev, { category, role }]);
    }
  };

  // Add a custom role
  const addCustomRole = () => {
    const trimmed = newCustomRole.trim();
    if (!trimmed) return;
    if (customRoles.includes(trimmed)) {
      toast.error('This role is already added');
      return;
    }
    setCustomRoles(prev => [...prev, trimmed]);
    setNewCustomRole('');
    setHasChanges(true);
  };

  // Remove a custom role
  const removeCustomRole = (role) => {
    setCustomRoles(prev => prev.filter(r => r !== role));
    setHasChanges(true);
  };

  // Iter-213+ P0 — autosave gig-role selections to localStorage so refresh
  // or crash never wipes the user's choices mid-onboarding.
  // Iter-281 P0 — scope drafts to the current logged-in user to prevent
  // admin impersonation from bleeding roles across talent profiles.
  useEffect(() => {
    if (hasChanges) {
      saveDraft(
        draftScope({ section: 'profile.gig_roles' }),
        { gig_roles: selectedRoles, custom_roles: customRoles, primary_title: primaryTitle },
      );
    }
  }, [selectedRoles, customRoles, primaryTitle, hasChanges]);

  // The talent's chosen primary title must be one of their selected roles
  // (predefined or custom) and never the generic "Other" bucket.
  const titleOptions = Array.from(new Set([
    ...selectedRoles.map((r) => r.role),
    ...customRoles,
  ].map((s) => (s || '').trim()).filter(Boolean))).filter((t) => t.toLowerCase() !== 'other');

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    const safeTitle = titleOptions.includes(primaryTitle) ? primaryTitle : '';
    const payload = { gig_roles: selectedRoles, custom_roles: customRoles, primary_title: safeTitle };
    const grScope = draftScope({ section: 'profile.gig_roles' });
    try {
      await api.put('/talent/profile/gig-roles', payload);
      clearDraft(grScope);
      toast.success('Gig roles saved successfully!');
      setHasChanges(false);
      // Notify ProfileInfo / Home compliance banner to re-fetch completion.
      window.dispatchEvent(new CustomEvent('talent-profile-updated'));
    } catch (error) {
      const info = handleSaveError(error, {
        scope: grScope,
        endpoint: '/talent/profile/gig-roles',
        payload,
      });
      toast.error(info.message);
    } finally {
      setSaving(false);
    }
  };

  // Count total selected roles
  const totalRoles = selectedRoles.length + customRoles.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="gig-roles-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="save-roles-btn"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Roles
              </>
            )}
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-cyan-100 rounded-xl">
            <Briefcase className="w-8 h-8 text-cyan-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Select Your Roles</h1>
            <p className="text-gray-500 mt-1">
              Pick every role you're qualified for. Clients see these on your profile, and you'll be
              matched with gigs that fit. Then choose one as your <strong>primary title</strong> — the
              headline shown on your shared booking card.
            </p>
          </div>
        </div>

        {/* Selection count */}
        <div className="flex items-center gap-3 mb-6">
          <Badge variant="outline" className="text-sm px-3 py-1">
            <Sparkles className="w-3 h-3 mr-1.5 text-cyan-500" />
            {totalRoles} role{totalRoles !== 1 ? 's' : ''} selected
          </Badge>
          {hasChanges && (
            <Badge className="bg-amber-100 text-amber-700">Unsaved changes</Badge>
          )}
        </div>

        {/* Info alert */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            <strong>Tip:</strong> Select multiple roles across different categories to increase your chances of being matched with more gigs. 
            You can also add custom roles under the "Other" section if your specialty isn't listed.
          </AlertDescription>
        </Alert>

        {/* Role Categories */}
        <div className="space-y-6">
          {Object.entries(categories).map(([categoryKey, categoryData]) => (
            <Card key={categoryKey} className="bg-white border-gray-200" data-testid={`category-${categoryKey}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-gray-900">{categoryData.label}</CardTitle>
                <CardDescription className="text-gray-500">
                  {categoryKey === 'other' 
                    ? 'Select from suggestions or add your own custom roles below'
                    : `Select the ${categoryData.label.toLowerCase()} roles you can perform`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {categoryData.roles.map((role) => {
                    const isSelected = isRoleSelected(categoryKey, role);
                    return (
                      <div
                        key={role}
                        onClick={() => toggleRole(categoryKey, role)}
                        className={`
                          flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all
                          ${isSelected 
                            ? 'bg-cyan-50 border-cyan-300 text-cyan-800' 
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                        data-testid={`role-${role.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          className={isSelected ? 'border-cyan-500 data-[state=checked]:bg-cyan-500' : ''}
                        />
                        <span className="text-sm font-medium">{role}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Custom Roles Section */}
        <Card className="mt-6 bg-white border-gray-200" data-testid="custom-roles-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-900">Custom Roles</CardTitle>
            <CardDescription className="text-gray-500">
              Add any other roles not listed above
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Existing custom roles */}
            {customRoles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {customRoles.map((role) => (
                  <Badge 
                    key={role} 
                    className="bg-purple-100 text-purple-800 px-3 py-1.5 text-sm flex items-center gap-1"
                    data-testid={`custom-role-${role.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {role}
                    <button 
                      onClick={() => removeCustomRole(role)}
                      className="ml-1 hover:text-purple-900"
                      data-testid={`remove-custom-role-${role.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Add new custom role */}
            <div className="flex gap-2">
              <Input
                value={newCustomRole}
                onChange={(e) => setNewCustomRole(e.target.value)}
                placeholder="Enter a custom role..."
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && addCustomRole()}
                data-testid="custom-role-input"
              />
              <Button 
                onClick={addCustomRole}
                variant="outline"
                disabled={!newCustomRole.trim()}
                data-testid="add-custom-role-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Primary Title — the single headline shown on the shared card */}
        <Card className="mt-6 bg-white border-cyan-200" data-testid="primary-title-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
              <Star className="w-5 h-5 text-cyan-600" /> Primary Title
            </CardTitle>
            <CardDescription className="text-gray-500">
              Choose one of your selected roles as the headline on your public booking card and social
              preview. This is display-only — it never changes your pay or matching.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {titleOptions.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="primary-title-empty">
                Select or add at least one role above to choose a primary title.
              </p>
            ) : (
              <Select
                value={primaryTitle && titleOptions.includes(primaryTitle) ? primaryTitle : '__none__'}
                onValueChange={(v) => { setPrimaryTitle(v === '__none__' ? '' : v); setHasChanges(true); }}
              >
                <SelectTrigger className="w-full sm:w-80" data-testid="primary-title-select">
                  <SelectValue placeholder="Choose your primary title" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="primary-title-opt-none">No primary title</SelectItem>
                  {titleOptions.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`primary-title-opt-${t.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>

        {/* Save button at bottom */}
        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-8"
            data-testid="save-roles-btn-bottom"
          >
            {saving ? 'Saving...' : 'Save Roles'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GigRolesEditor;
